import { Injectable, inject, signal } from '@angular/core';
import {
  DataIngestionService,
  WalletTrendMonthResponseDto,
} from 'src/generated-sources/openapi/data-ingestion/data-ingestion';
import {
  PlanIndicatorFindByPlanIdLightResponseDto,
  PlanIndicatorService,
} from 'src/generated-sources/openapi/configuration-service/plan-indicator';

import { getMonthName } from '../../helpers/date-utils';
import { ChartConfigService, ChartData } from '../attribute-evolution/chart-config.service';
import { IndicatorCategory } from 'src/app/services/models/enums';
import { IndicatorDataService } from 'src/app/services/data/indicator-data.service';

export interface WalletTrendIndicatorDto {
  id: string;
  name: string;
  group?: string;
  displayLabel: string;
}

@Injectable({ providedIn: 'any' })
export class WalletTrendFacade {
  private readonly dataIngestionService = inject(DataIngestionService);
  private readonly planIndicatorService = inject(PlanIndicatorService);
  private readonly chartConfigService = inject(ChartConfigService);
  private readonly indicatorDataService = inject(IndicatorDataService);

  readonly indicators = signal<WalletTrendIndicatorDto[]>([]);
  readonly selectedIndicator = signal<WalletTrendIndicatorDto | null>(null);
  readonly chartData = signal<ChartData>({ labels: [], data: { values: [], label: '', type: '' } });
  readonly isLoadingIndicators = signal(false);
  readonly isLoadingChart = signal(false);
  readonly hasSearched = signal(false);
  readonly error = signal<string | null>(null);

  loadIndicators(planId: string): void {
    if (!planId) {
      this.error.set($localize`:@@js_ErrorGettingIndicators:Error al recuperar indicadores`);
      return;
    }

    this.isLoadingIndicators.set(true);
    this.error.set(null);

    this.planIndicatorService.findByPlanIdLight({ planId }).subscribe({
      next: (response: PlanIndicatorFindByPlanIdLightResponseDto) => {
        const indicators: WalletTrendIndicatorDto[] = (response.items || [])
          .filter((item) => item.category == IndicatorCategory.Quantity && item.group !== 'Score')
          .map((item) => ({
            id: item.id,
            name: item.name,
            group: item.group,
            displayLabel: item.group
              ? $localize`:@@walletTrendIndexLabel:Índice ` + item.group
              : item.name,
          }));
        this.indicators.set(this.indicatorDataService.sortByDisplayOrder(indicators));
        this.selectedIndicator.set(null);
        this.isLoadingIndicators.set(false);
      },
      error: () => {
        this.error.set($localize`:@@js_ErrorGettingIndicators:Error al recuperar indicadores`);
        this.isLoadingIndicators.set(false);
      },
    });
  }

  loadChartData(walletId: string, indicatorId: string, startDate: Date, endDate: Date): void {
    this.isLoadingChart.set(true);
    this.error.set(null);

    const startMonth = this.formatToDateString(startDate);
    const endMonth = this.formatToDateString(endDate);

    this.dataIngestionService
      .listWalletTrendMonth({ indicatorId, walletId, startMonth, endMonth })
      .subscribe({
        next: (response: WalletTrendMonthResponseDto[]) => {
          const results = response || [];
          if (results.length === 0) {
            this.chartData.set(this.chartConfigService.createEmptyChartData());
          } else {
            this.chartData.set(this.processChartResults(results, startDate, endDate));
          }
          this.isLoadingChart.set(false);
          this.hasSearched.set(true);
        },
        error: () => {
          this.error.set(
            $localize`:@@js_ErrorGettingChartData:Error al recuperar datos del gráfico`,
          );
          this.isLoadingChart.set(false);
        },
      });
  }

  setSelectedIndicator(indicator: WalletTrendIndicatorDto | null): void {
    this.selectedIndicator.set(indicator);
  }

  resetChartData(): void {
    this.chartData.set(this.chartConfigService.createEmptyChartData());
    this.hasSearched.set(false);
    this.error.set(null);
  }

  private formatToDateString(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    return `${year}-${month}-01`;
  }

  private processChartResults(
    results: WalletTrendMonthResponseDto[],
    startDate: Date,
    endDate: Date,
  ): ChartData {
    const chartPoints = results.map((r) => ({
      ingestionMonth: r.reference_month || '',
      value: r.value != null ? r.value.toString() : '0',
    }));

    const range: { date: Date; label: string }[] = [];
    let d = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    const end = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
    while (d <= end) {
      const label = `${getMonthName(d.getMonth())} ${d.getFullYear()}`;
      range.push({ date: new Date(d), label });
      d.setMonth(d.getMonth() + 1);
    }

    return this.chartConfigService.processChartData(
      chartPoints,
      this.selectedIndicator()?.displayLabel,
      range,
    );
  }
}
